import React, { useEffect, useState, useRef, useCallback } from 'react';
import api from '../api';
import { writePrintDoc } from '../utils/printUtils';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import SearchableSelect from '../components/SearchableSelect';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Edit, Trash2, Printer, FileUp, Download, FileCheck, Camera, X, Settings, Eye } from 'lucide-react';
import { useAuth } from '../AuthContext';
import { useNavigate } from 'react-router-dom';
import DeleteConfirmDialog from '../components/DeleteConfirmDialog';
import PaginationControls from '../components/PaginationControls';
import ViewBillModal from '../components/ViewBillModal';
import useAutoRefresh from '../hooks/useAutoRefresh';

const Expenditures = () => {
  const { isAdmin, user } = useAuth();
  const navigate = useNavigate();
  const canViewAll = isAdmin() || ['Checker', 'Account', 'HR'].includes(user?.role);
  const extraAllowed = Array.isArray(user?.allowed_branch_ids) ? user.allowed_branch_ids : [];
  // Users can pick a block if they see all OR have been granted extra branches
  const canSelectBranch = canViewAll || extraAllowed.length > 0;
  const [expenditures, setExpenditures] = useState([]);
  const [branches, setBranches] = useState([]);
  const [projectActivities, setProjectActivities] = useState([]);
  const [expenditureHeads, setExpenditureHeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedExpenditure, setSelectedExpenditure] = useState(null);
  const [nextVoucherNumber, setNextVoucherNumber] = useState('');
  const [billFile, setBillFile] = useState(null);
  const billInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [billMode, setBillMode] = useState('upload'); // 'upload' or 'camera'
  const streamRef = useRef(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [selectedBranchFilter, setSelectedBranchFilter] = useState('all');
  const [summaryDialog, setSummaryDialog] = useState({ open: false, exp: null });
  const [viewBill, setViewBill] = useState({ open: false, id: null, filename: null });

  const [formData, setFormData] = useState({
    project_activity_id: '',
    branch_id: '',
    expenditure_head_id: '',
    category: '',
    amount: 0,
    description: '',
    date: new Date().toISOString().split('T')[0],
    voucher_number: '',
  });

  useEffect(() => {
    fetchExpenditures();
    fetchBranches();
    fetchMasters();
  }, []);

  // Pagination
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    if (branches.length > 0) fetchExpenditures();
    // eslint-disable-next-line
  }, [selectedBranchFilter, page, pageSize]);

  // Real-time auto-refresh
  useAutoRefresh(() => fetchExpenditures());

  const fetchBranches = async () => {
    try { const r = await api.get('/branches'); setBranches(r.data); } catch { /* ignore */ }
  };

  const fetchMasters = async () => {
    try {
      const [a, h] = await Promise.all([
        api.get('/project-activities'),
        api.get('/expenditure-heads'),
      ]);
      setProjectActivities(a.data || []);
      setExpenditureHeads(h.data || []);
    } catch {
      // silently fail — page still usable if masters unavailable
    }
  };

  useEffect(() => {
    if (dialogOpen && !editMode) generateVoucherNumber();
  }, [dialogOpen, expenditures, editMode]);

  const generateVoucherNumber = () => {
    const year = new Date().getFullYear();
    const count = expenditures.length + 1;
    const voucherNum = `VOUCH-${year}-${String(count).padStart(4, '0')}`;
    setNextVoucherNumber(voucherNum);
    setFormData(prev => ({ ...prev, voucher_number: voucherNum }));
  };

  const fetchExpenditures = async () => {
    try {
      const params = { skip: page * pageSize, limit: pageSize };
      if (canViewAll && selectedBranchFilter !== 'all') params.branch_id = selectedBranchFilter;
      const response = await api.get('/expenditures', { params });
      setExpenditures(response.data);
      setTotalCount(parseInt(response.headers?.['x-total-count'] || '0', 10) || response.data.length);
    } catch { toast.error('Failed to fetch expenditures'); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required masters
    if (!formData.project_activity_id) {
      toast.error('Please select a Project Activity');
      return;
    }
    if (!formData.expenditure_head_id) {
      toast.error('Please select an Expenditure Head');
      return;
    }
    if (canSelectBranch && !formData.branch_id) {
      toast.error('Please select a Block (Branch)');
      return;
    }

    // Validate bill file for new expenditures
    if (!editMode && !billFile) {
      toast.error('Bill/Voucher document is mandatory. Please upload a file.');
      return;
    }

    try {
      // Attach category name from selected head for backward-compat
      const headObj = expenditureHeads.find((h) => h.id === formData.expenditure_head_id);
      const payload = {
        ...formData,
        category: headObj?.name || formData.category || '',
        date: new Date(formData.date).toISOString(),
      };
      const fd = new FormData();
      fd.append('data', JSON.stringify(payload));

      if (billFile) {
        fd.append('bill_file', billFile);
      }

      if (editMode && selectedExpenditure) {
        await api.put(`/expenditures/${selectedExpenditure.id}`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        toast.success('Expenditure updated successfully');
      } else {
        await api.post('/expenditures', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        toast.success('Expenditure recorded successfully');
      }
      closeDialog();
      fetchExpenditures();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save expenditure');
    }
  };

  const handleEdit = (exp) => {
    setSelectedExpenditure(exp);
    setFormData({
      project_activity_id: exp.project_activity_id || '',
      branch_id: exp.branch_id || '',
      expenditure_head_id: exp.expenditure_head_id || '',
      category: exp.category || '',
      amount: exp.amount,
      description: exp.description,
      date: new Date(exp.date).toISOString().split('T')[0],
      voucher_number: exp.voucher_number,
    });
    setBillFile(null);
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/expenditures/${id}`);
      toast.success('Expenditure deleted successfully');
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      fetchExpenditures();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete expenditure');
    }
  };

  const handleDownloadBill = async (expId, filename) => {
    try {
      const resp = await api.get(`/expenditures/${expId}/bill`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || 'bill_voucher');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Bill downloaded');
    } catch {
      toast.error('Failed to download bill');
    }
  };

  const openDeleteDialog = (exp) => { setItemToDelete(exp); setDeleteDialogOpen(true); };

  const startCamera = useCallback(async () => {
    try {
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 960 } } });
      } catch {
        // Fallback: try without facingMode constraint (works on laptops/desktops)
        stream = await navigator.mediaDevices.getUserMedia({ video: true });
      }
      streamRef.current = stream;
      setCameraActive(true);
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {});
        }
      }, 150);
    } catch (err) {
      console.error('Camera error:', err);
      toast.error('Unable to access camera. Please use file upload instead.');
      setBillMode('upload');
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraActive(false);
  }, []);

  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
    setCapturedImage(dataUrl);
    stopCamera();

    // Convert to File for upload
    fetch(dataUrl)
      .then(res => res.blob())
      .then(blob => {
        const file = new File([blob], `bill_capture_${Date.now()}.jpg`, { type: 'image/jpeg' });
        setBillFile(file);
      });
  }, [stopCamera]);

  const retakePhoto = useCallback(() => {
    setCapturedImage(null);
    setBillFile(null);
    startCamera();
  }, [startCamera]);

  const closeDialog = () => {
    stopCamera();
    setDialogOpen(false);
    setEditMode(false);
    setSelectedExpenditure(null);
    setBillFile(null);
    setCapturedImage(null);
    setBillMode('upload');
    if (billInputRef.current) billInputRef.current.value = '';
    setFormData({
      project_activity_id: '',
      branch_id: '',
      expenditure_head_id: '',
      category: '',
      amount: 0,
      description: '',
      date: new Date().toISOString().split('T')[0],
      voucher_number: '',
    });
  };

  return (
    <div className="p-6 md:p-8 lg:p-10" data-testid="expenditures-page">
      <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Expenditures</h1>
          <p className="text-lg text-slate-600">Track and manage organizational expenses</p>
        </div>

        <div className="flex flex-wrap gap-2 items-center">
          {isAdmin() && (
            <Button
              variant="outline"
              onClick={() => navigate('/expenditure-masters')}
              data-testid="manage-masters-button"
            >
              <Settings className="w-4 h-4 mr-2" />
              Manage Masters
            </Button>
          )}
          {canViewAll && (
            <Select value={selectedBranchFilter} onValueChange={setSelectedBranchFilter}>
              <SelectTrigger className="w-48" data-testid="branch-filter-expenditures">
                <SelectValue placeholder="All Branches" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Branches</SelectItem>
                {branches.map(b => (
                  <SelectItem key={b.id} value={b.id}>{b.code} - {b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

        <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) closeDialog(); else setDialogOpen(true); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-expenditure-button" className="bg-emerald-700 hover:bg-emerald-800 text-white">
              <Plus className="w-4 h-4 mr-2" />
              Add Expenditure
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editMode ? 'Edit Expenditure' : 'Record Expenditure'}</DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Step 1: Project Activity */}
              <div className="space-y-2">
                <Label htmlFor="project-activity">Project Activity <span className="text-red-600">*</span></Label>
                {projectActivities.length === 0 ? (
                  <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500">
                    No activities. {isAdmin() && 'Add them in Expenditure Masters.'}
                  </div>
                ) : (
                  <SearchableSelect
                    value={formData.project_activity_id}
                    onChange={(value) => setFormData({ ...formData, project_activity_id: value })}
                    options={projectActivities.map((a) => ({ value: a.id, label: a.name }))}
                    placeholder="Select Project Activity — type to search"
                    testId="project-activity-select"
                  />
                )}
              </div>

              {/* Step 2: Block (Branch) */}
              <div className="space-y-2">
                <Label htmlFor="block-select">Block <span className="text-red-600">*</span></Label>
                {canSelectBranch ? (
                  <Select
                    value={formData.branch_id}
                    onValueChange={(value) => setFormData({ ...formData, branch_id: value })}
                  >
                    <SelectTrigger data-testid="block-select">
                      <SelectValue placeholder="Select Block" />
                    </SelectTrigger>
                    <SelectContent>
                      {(canViewAll
                        ? branches
                        : branches.filter((b) => b.id === user?.branch_id || extraAllowed.includes(b.id))
                      ).map((b) => (
                        <SelectItem key={b.id} value={b.id}>
                          {b.code} - {b.name}
                          {b.id === user?.branch_id ? ' (Home)' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    value={branches.find((b) => b.id === user?.branch_id)?.name || user?.branch_id || '—'}
                    readOnly
                    disabled
                    className="bg-slate-50 cursor-not-allowed"
                    data-testid="block-readonly"
                  />
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Step 3: Expenditure Head */}
                <div className="space-y-2">
                  <Label htmlFor="expenditure-head">Expenditure Head <span className="text-red-600">*</span></Label>
                  {expenditureHeads.length === 0 ? (
                    <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500">
                      No heads defined
                    </div>
                  ) : (
                    <SearchableSelect
                      value={formData.expenditure_head_id}
                      onChange={(value) => setFormData({ ...formData, expenditure_head_id: value })}
                      options={expenditureHeads.map((h) => ({ value: h.id, label: h.name }))}
                      placeholder="Select Head — type to search"
                      testId="expenditure-head-select"
                    />
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="amount">Amount (Rs.) *</Label>
                  <Input
                    id="amount"
                    type="number"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) })}
                    required
                    data-testid="amount-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="date">Date *</Label>
                  <Input
                    id="date"
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="voucher_number">Voucher Number {!editMode && '(Auto-generated)'}</Label>
                  <Input
                    id="voucher_number"
                    value={formData.voucher_number}
                    readOnly={!editMode}
                    disabled={!editMode}
                    onChange={(e) => editMode && setFormData({ ...formData, voucher_number: e.target.value })}
                    data-testid="voucher-input"
                    className={!editMode ? "bg-slate-50 cursor-not-allowed" : ""}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  data-testid="description-input"
                />
              </div>

              {/* Bill/Voucher Upload - Mandatory */}
              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  Bill / Voucher Document <span className="text-red-600 font-bold">*</span>
                  {editMode && selectedExpenditure?.has_bill && !billFile && (
                    <span className="text-xs text-emerald-600 font-normal ml-2 flex items-center gap-1">
                      <FileCheck className="w-3 h-3" /> Existing bill attached
                    </span>
                  )}
                </Label>

                {/* Toggle: Upload File / Capture from Camera */}
                <div className="flex gap-1 mb-2">
                  <button
                    type="button"
                    onClick={() => { setBillMode('upload'); stopCamera(); setCapturedImage(null); }}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 text-xs font-medium rounded-lg border transition-colors ${
                      billMode === 'upload' ? 'bg-emerald-700 text-white border-emerald-700' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
                    }`}
                    data-testid="bill-mode-upload"
                  >
                    <FileUp className="w-3.5 h-3.5" /> Upload File
                  </button>
                  <button
                    type="button"
                    onClick={() => { setBillMode('camera'); setBillFile(null); if (billInputRef.current) billInputRef.current.value = ''; startCamera(); }}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 text-xs font-medium rounded-lg border transition-colors ${
                      billMode === 'camera' ? 'bg-emerald-700 text-white border-emerald-700' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
                    }`}
                    data-testid="bill-mode-camera"
                  >
                    <Camera className="w-3.5 h-3.5" /> Capture from Camera
                  </button>
                </div>

                {/* Upload File Mode */}
                {billMode === 'upload' && (
                  <div
                    className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                      billFile ? 'border-emerald-400 bg-emerald-50' : 'border-slate-300 hover:border-emerald-400 hover:bg-emerald-50/30'
                    }`}
                    onClick={() => billInputRef.current?.click()}
                    data-testid="bill-upload-area"
                  >
                    <input
                      type="file"
                      ref={billInputRef}
                      onChange={(e) => {
                        if (e.target.files[0]) { setBillFile(e.target.files[0]); setCapturedImage(null); }
                      }}
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                      className="hidden"
                      data-testid="bill-file-input"
                    />
                    {billFile && !capturedImage ? (
                      <div className="flex items-center justify-center gap-2 text-emerald-700">
                        <FileCheck className="w-5 h-5" />
                        <span className="text-sm font-medium">{billFile.name}</span>
                        <span className="text-xs text-emerald-500">({(billFile.size / 1024).toFixed(1)} KB)</span>
                      </div>
                    ) : (
                      <div className="text-slate-500">
                        <FileUp className="w-8 h-8 mx-auto mb-1 text-slate-400" />
                        <p className="text-sm font-medium">Click to upload Bill / Voucher</p>
                        <p className="text-xs text-slate-400 mt-1">PDF, JPG, PNG, DOC, XLS accepted</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Camera Capture Mode */}
                {billMode === 'camera' && (
                  <div className="rounded-lg border-2 border-slate-300 overflow-hidden bg-black" data-testid="camera-capture-area">
                    <canvas ref={canvasRef} className="hidden" />

                    {capturedImage ? (
                      <div className="relative">
                        <img src={capturedImage} alt="Captured bill" className="w-full max-h-64 object-contain bg-slate-900" />
                        <div className="absolute top-2 right-2 flex gap-1">
                          <button
                            type="button"
                            onClick={() => { setCapturedImage(null); setBillFile(null); }}
                            className="bg-red-600 text-white rounded-full p-1.5 hover:bg-red-700 shadow-lg"
                            data-testid="remove-capture-btn"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="bg-emerald-50 border-t border-emerald-200 p-2 flex items-center justify-between">
                          <span className="text-xs text-emerald-700 flex items-center gap-1"><FileCheck className="w-3.5 h-3.5" /> Photo captured</span>
                          <Button type="button" size="sm" variant="outline" onClick={retakePhoto} className="h-7 text-xs" data-testid="retake-btn">
                            <Camera className="w-3 h-3 mr-1" /> Retake
                          </Button>
                        </div>
                      </div>
                    ) : cameraActive ? (
                      <div className="relative">
                        <video ref={videoRef} autoPlay playsInline muted className="w-full max-h-64 object-contain bg-slate-900" />
                        <div className="bg-slate-800 p-3 flex justify-center">
                          <button
                            type="button"
                            onClick={capturePhoto}
                            className="w-14 h-14 rounded-full border-4 border-white bg-red-500 hover:bg-red-600 transition-colors shadow-lg flex items-center justify-center"
                            data-testid="capture-btn"
                          >
                            <Camera className="w-6 h-6 text-white" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="p-8 text-center text-slate-400">
                        <Camera className="w-10 h-10 mx-auto mb-2" />
                        <p className="text-sm">Starting camera...</p>
                      </div>
                    )}
                  </div>
                )}

                {!editMode && !billFile && (
                  <p className="text-xs text-red-500">Bill/Voucher upload is mandatory for new expenditures</p>
                )}
              </div>

              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={closeDialog}>Cancel</Button>
                <Button
                  type="submit"
                  data-testid="submit-expenditure-button"
                  className="bg-emerald-700 hover:bg-emerald-800 text-white"
                >
                  {editMode ? 'Update Expenditure' : 'Record Expenditure'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-700"></div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex-1 min-h-[calc(100vh-280px)]">
          {canViewAll && expenditures.length > 0 && (
            <div className="p-3 border-b bg-slate-50 flex justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const sortedExp = [...expenditures].sort((a, b) => new Date(b.date) - new Date(a.date));
                  const htmlContent = `<html><head><title>Expenditures Report - WWAGS MIS</title><style>body{font-family:Arial,sans-serif;padding:20px}h1{color:#047857;font-size:20px;margin-bottom:5px}p{color:#666;font-size:12px;margin-bottom:15px}table{width:100%;border-collapse:collapse;font-size:11px}th{background:#ea580c;color:white;padding:8px;text-align:left}td{padding:6px 8px;border-bottom:1px solid #ddd}tr:nth-child(even){background:#f9f9f9}.amount{text-align:right;font-weight:bold}.total{background:#fff7ed;font-weight:bold}@media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}</style></head><body><h1>WWAGS MIS - Expenditures Report</h1><p>Generated: ${new Date().toLocaleString('en-IN')} | Entries: ${sortedExp.length} (Latest first)</p><table><thead><tr><th>Date</th><th>Branch</th><th>Category</th><th>Description</th><th class="amount">Amount</th><th>Voucher</th><th>Bill</th></tr></thead><tbody>${sortedExp.map(exp => `<tr><td>${new Date(exp.date).toLocaleDateString('en-IN')}</td><td>${branches.find(b => b.id === exp.branch_id)?.name || '-'}</td><td>${exp.category}</td><td>${exp.description || '-'}</td><td class="amount">Rs.${exp.amount.toLocaleString('en-IN')}</td><td>${exp.voucher_number}</td><td>${exp.has_bill ? 'Yes' : 'No'}</td></tr>`).join('')}<tr class="total"><td colspan="4" style="text-align:right;">Total:</td><td class="amount">Rs.${sortedExp.reduce((s, e) => s + e.amount, 0).toLocaleString('en-IN')}</td><td></td><td></td></tr></tbody></table></body></html>`;
                  const pw = window.open('', '_blank');
                  if (pw) { writePrintDoc(pw.document, htmlContent); setTimeout(() => pw.print(), 500); }
                }}
                data-testid="print-expenditures-button"
              >
                <Printer className="w-4 h-4 mr-2" />Print Report
              </Button>
            </div>
          )}
          <div className="overflow-auto h-full max-h-[calc(100vh-280px)]">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Date</th>
                  {canViewAll && <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Branch</th>}
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Category</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Description</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Voucher</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Bill</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {expenditures.length === 0 ? (
                  <tr>
                    <td colSpan={canViewAll ? 8 : 7} className="px-4 py-8 text-center text-slate-500">
                      No expenditures recorded
                    </td>
                  </tr>
                ) : (
                  expenditures.map((exp) => (
                    <tr key={exp.id} className="hover:bg-slate-50 transition-colors" data-testid="expenditure-row">
                      <td className="px-4 py-3 text-sm text-slate-700">
                        {new Date(exp.date).toLocaleDateString('en-IN')}
                      </td>
                      {canViewAll && (
                        <td className="px-4 py-3 text-xs">
                          <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700">
                            {branches.find(b => b.id === exp.branch_id)?.name || '-'}
                          </span>
                        </td>
                      )}
                      <td className="px-4 py-3">
                        <span className="text-xs px-2.5 py-0.5 rounded-full font-medium bg-orange-100 text-orange-800 border border-orange-200">
                          {exp.category}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700">{exp.description}</td>
                      <td className="px-4 py-3 text-sm font-semibold text-slate-900">
                        Rs.{exp.amount.toLocaleString('en-IN')}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <button
                          onClick={() => setSummaryDialog({ open: true, exp })}
                          className="text-blue-700 hover:text-blue-900 hover:underline font-medium cursor-pointer"
                          data-testid={`voucher-link-${exp.id}`}
                        >
                          {exp.voucher_number}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        {exp.has_bill ? (
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setViewBill({ open: true, id: exp.id, filename: exp.bill_filename })}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 h-7 px-2"
                              title="View bill (no download)"
                              data-testid={`view-bill-${exp.id}`}
                            >
                              <Eye className="w-3.5 h-3.5 mr-1" />
                              <span className="text-xs">View</span>
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDownloadBill(exp.id, exp.bill_filename)}
                              className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 h-7 px-2"
                              title="Download bill"
                              data-testid={`download-bill-${exp.id}`}
                            >
                              <Download className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">No bill</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          {isAdmin() && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEdit(exp)}
                                data-testid="edit-expenditure-button"
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openDeleteDialog(exp)}
                                data-testid="delete-expenditure-button"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          {!isAdmin() && (
                            <span className="text-xs text-slate-400 italic">View only</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <PaginationControls
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={setPage}
            onPageSizeChange={(n) => { setPageSize(n); setPage(0); }}
            label="expenditures"
          />
        </div>
      )}

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={() => itemToDelete && handleDelete(itemToDelete.id)}
        title="Delete Expenditure"
        description="Are you sure you want to delete this expenditure record? This action cannot be undone."
        itemName={`${itemToDelete?.category} - Rs.${itemToDelete?.amount?.toLocaleString('en-IN')}`}
      />

      {/* Expenditure Summary Dialog */}
      <Dialog open={summaryDialog.open} onOpenChange={(o) => { if (!o) setSummaryDialog({ open: false, exp: null }); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Expenditure Summary</DialogTitle>
          </DialogHeader>
          {summaryDialog.exp && (
            <div className="space-y-3">
              <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200 text-center">
                <p className="text-xs text-emerald-600 font-medium">Voucher No.</p>
                <p className="text-xl font-bold text-emerald-900">{summaryDialog.exp.voucher_number}</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-500">Date</p>
                  <p className="text-sm font-semibold text-slate-900">{new Date(summaryDialog.exp.date).toLocaleDateString('en-IN', { day:'2-digit', month:'long', year:'numeric' })}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-500">Amount</p>
                  <p className="text-sm font-bold text-emerald-800">Rs.{summaryDialog.exp.amount?.toLocaleString('en-IN')}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 col-span-2">
                  <p className="text-xs text-slate-500">Project Activity</p>
                  <p className="text-sm font-semibold text-slate-900">
                    {summaryDialog.exp.project_activity_name
                      || projectActivities.find((a) => a.id === summaryDialog.exp.project_activity_id)?.name
                      || <span className="italic text-slate-400">—</span>}
                  </p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-500">Expenditure Head</p>
                  <p className="text-sm font-semibold text-slate-900">
                    {expenditureHeads.find((h) => h.id === summaryDialog.exp.expenditure_head_id)?.name
                      || summaryDialog.exp.category
                      || '-'}
                  </p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-500">Block (Branch)</p>
                  <p className="text-sm font-semibold text-slate-900">{branches.find(b => b.id === summaryDialog.exp.branch_id)?.name || summaryDialog.exp.branch_id || '-'}</p>
                </div>
              </div>
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs text-slate-500">Description</p>
                <p className="text-sm text-slate-900">{summaryDialog.exp.description}</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                <p className="text-xs text-blue-600 font-medium uppercase tracking-wide">Data Entry By</p>
                <p className="text-sm font-semibold text-slate-900 mt-0.5">
                  {summaryDialog.exp.created_by_email || <span className="italic text-slate-400">unknown</span>}
                </p>
                <p className="text-[10px] text-slate-500 font-mono mt-0.5">
                  ID: {summaryDialog.exp.created_by || '—'}
                </p>
                {summaryDialog.exp.created_at && (
                  <p className="text-[10px] text-slate-400 mt-0.5">
                    Entered on {new Date(summaryDialog.exp.created_at).toLocaleString('en-IN')}
                  </p>
                )}
              </div>
              {summaryDialog.exp.has_bill && (
                <div className="flex justify-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setViewBill({ open: true, id: summaryDialog.exp.id, filename: summaryDialog.exp.bill_filename })}
                    className="text-blue-700 border-blue-200"
                  >
                    <Eye className="w-4 h-4 mr-2" />View Bill
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleDownloadBill(summaryDialog.exp.id, summaryDialog.exp.bill_filename)} className="text-emerald-700 border-emerald-200">
                    <Download className="w-4 h-4 mr-2" />Download ({summaryDialog.exp.bill_filename})
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ViewBillModal
        open={viewBill.open}
        onOpenChange={(o) => setViewBill({ open: o, id: o ? viewBill.id : null, filename: o ? viewBill.filename : null })}
        expenditureId={viewBill.id}
        filename={viewBill.filename}
      />
    </div>
  );
};

export default Expenditures;
